/**
 * V320: Normalize profile photo URLs – single source for ordering and Cloudinary preference.
 * Used by both server pipeline and UI (Profile, PhotoContext).
 *
 * Rules:
 * - First priority: unifiedUrl if it is Cloudinary
 * - Next: dbUrls filtered to Cloudinary only, keeping existing order
 * - If none and authAvatarUrl is googleusercontent: DO NOT return it as final (must go through Cloudinary pipeline)
 * - Deduplicate; primary is index 0
 */

const CLOUDINARY_HOST = 'cloudinary.com';

export function isCloudinaryUrl(url: string | null | undefined): boolean {
  return !!url && typeof url === 'string' && url.includes(CLOUDINARY_HOST);
}

export function isGooglePlaceholderUrl(url: string | null | undefined): boolean {
  return !!url && typeof url === 'string' && (url.includes('googleusercontent.com') || url.includes('lh3.googleusercontent'));
}

export interface NormalizePhotoUrlsInput {
  /** URLs from DB (users.photo_urls) – may contain Google placeholders */
  dbUrls?: string[] | null;
  /** Unified/cache URL from PhotoContext or useProfilePhoto */
  unifiedUrl?: string | null;
  /** Auth provider avatar (user_metadata.avatar_url or picture) */
  authAvatarUrl?: string | null;
}

/**
 * Returns a stable, deduplicated list of photo URLs with Cloudinary preferred.
 * Never returns a Google placeholder as a "final" URL (UI should show skeleton and trigger conversion).
 */
export function normalizePhotoUrls(input: NormalizePhotoUrlsInput): string[] {
  const { dbUrls = [], unifiedUrl, authAvatarUrl } = input;

  const arr = Array.isArray(dbUrls) ? dbUrls : [];
  const cloudinaryFromDb = arr.filter((u): u is string => typeof u === 'string' && isCloudinaryUrl(u));
  const seen = new Set<string>();
  const result: string[] = [];

  // 1) First priority: unifiedUrl if Cloudinary
  if (unifiedUrl && isCloudinaryUrl(unifiedUrl) && !seen.has(unifiedUrl)) {
    seen.add(unifiedUrl);
    result.push(unifiedUrl);
  }

  // 2) Then: dbUrls (Cloudinary only), keep order
  for (const u of cloudinaryFromDb) {
    if (!seen.has(u)) {
      seen.add(u);
      result.push(u);
    }
  }

  // 3) If we have nothing and authAvatarUrl is Google: do NOT add it (must go through Cloudinary pipeline)
  if (result.length === 0 && authAvatarUrl && isGooglePlaceholderUrl(authAvatarUrl)) {
    return []; // Caller should show placeholder and optionally trigger bootstrap
  }

  return result;
}
